import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase.config';
import { doc, getDoc } from 'firebase/firestore';

const useAuth = () => {
  // Initialize from cache so there is ZERO flash of unauthenticated state or missing avatar/name
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedAuthProfile');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(!currentUser);
  // `verified` becomes true only after Firebase Auth has reported the session and the
  // role has been re-read from Firestore. The cached profile (localStorage) can be
  // edited by anyone in their own browser, so it must never be trusted for access checks.
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Fetch complete profile from Firestore
          const userDocSnap = await getDoc(doc(db, 'users', user.uid));
          const firestoreData = userDocSnap.exists() ? userDocSnap.data() : null;

          const profileData = {
            uid: user.uid,
            email: user.email,
            displayName: firestoreData?.displayName || user.displayName || user.email?.split('@')[0],
            photoURL: firestoreData?.photoURL || user.photoURL || null,
            role: firestoreData?.role || 'customer',
          };

          localStorage.setItem('cachedAuthProfile', JSON.stringify(profileData));
          setCurrentUser(profileData);
        } catch (err) {
          console.error('Error fetching user document in useAuth:', err);
          // Fall back to a plain customer profile; never keep a cached role.
          setCurrentUser({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email?.split('@')[0],
            photoURL: user.photoURL || null,
            role: 'customer',
          });
        }
      } else {
        localStorage.removeItem('cachedAuthProfile');
        setCurrentUser(null);
      }
      setLoading(false);
      setVerified(true);
    });

    return () => unsubscribe();
  }, []);

  return {
    currentUser,
    loading,
    verified,
  };
};

export default useAuth;